import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LoadingScreen = ({ onLoadingComplete }) => {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [phase, setPhase] = useState('loading'); // 'loading', 'reveal', 'exit'

  // Generate ink particles
  const inkParticles = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    startX: 40 + Math.random() * 20,
    startY: 30 + Math.random() * 40,
    delay: Math.random() * 2,
    duration: 2 + Math.random() * 3,
    size: 4 + Math.random() * 8,
  }));

  // Simulate loading progress
  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + Math.random() * 4 + 2;
      });
    }, 50);

    return () => clearInterval(interval);
  }, []);

  // Handle phase transitions
  useEffect(() => {
    if (loadingProgress >= 100 && phase === 'loading') {
      setTimeout(() => setPhase('reveal'), 600);
    }
    if (phase === 'reveal') {
      setTimeout(() => setPhase('exit'), 1200);
    }
    if (phase === 'exit') {
      setTimeout(() => onLoadingComplete(), 800);
    }
  }, [loadingProgress, phase, onLoadingComplete]);

  return (
    <AnimatePresence>
      {phase !== 'done' && (
        <motion.div 
          className="fixed inset-0 z-[100] overflow-hidden bg-zinc-950"
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: 'easeInOut' }}
        >
          {/* Dark background with subtle crimson radial */}
          <div className="absolute inset-0 bg-gradient-to-b from-black via-zinc-950 to-black" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_30%,rgba(139,0,0,0.15),transparent_60%)]" />
          
          {/* Ink splatter particles */}
          {inkParticles.map((particle) => (
            <motion.div
              key={particle.id}
              className="absolute rounded-full bg-zinc-800/50"
              style={{
                left: `${particle.startX}%`,
                top: `${particle.startY}%`,
                width: particle.size,
                height: particle.size,
              }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{
                scale: [0, 1.5, 1],
                opacity: [0, 0.6, 0],
                y: [0, 50, 100],
              }}
              transition={{
                duration: particle.duration,
                delay: particle.delay,
                repeat: Infinity,
                ease: 'easeOut',
              }}
            />
          ))}

          {/* Main content container */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            
            {/* Samurai Image Container */}
            <motion.div
              className="relative"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ 
                scale: phase === 'exit' ? 1.1 : 1, 
                opacity: phase === 'exit' ? 0 : 1,
                y: 0,
              }}
              transition={{ 
                duration: phase === 'exit' ? 0.6 : 1.2, 
                ease: [0.25, 0.46, 0.45, 0.94] 
              }}
            >
              {/* Crimson glow behind samurai */}
              <motion.div
                className="absolute inset-0 blur-3xl"
                style={{
                  background: 'radial-gradient(circle, rgba(139,0,0,0.4) 0%, rgba(80,0,0,0.2) 50%, transparent 70%)',
                  transform: 'scale(1.3)',
                }}
                animate={{
                  opacity: [0.4, 0.7, 0.4],
                  scale: [1.3, 1.5, 1.3],
                }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              />
              
              {/* Samurai Image */}
              <motion.img
                src="/tsusima.png"
                alt="Samurai"
                className="relative w-64 h-auto md:w-80 object-contain drop-shadow-[0_0_40px_rgba(139,0,0,0.5)]"
                style={{ filter: 'brightness(1.1) contrast(1.1)' }}
                initial={{ filter: 'brightness(0) contrast(1)' }}
                animate={{ 
                  filter: 'brightness(1.1) contrast(1.1)',
                }}
                transition={{ duration: 1.5, ease: 'easeOut' }}
              />

              {/* Ink drip effect from bottom */}
              <motion.div
                className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-32 h-16"
                style={{
                  background: 'linear-gradient(to bottom, rgba(20,20,20,0.8) 0%, transparent 100%)',
                  filter: 'blur(8px)',
                }}
                initial={{ opacity: 0, scaleY: 0 }}
                animate={{ opacity: 0.6, scaleY: 1 }}
                transition={{ duration: 1, delay: 0.5 }}
              />
            </motion.div>

            {/* Loading text and progress - Brush stroke style */}
            <motion.div
              className="mt-8 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ 
                opacity: phase === 'exit' ? 0 : 1, 
                y: phase === 'exit' ? -20 : 0 
              }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {/* Japanese text - brush stroke style */}
              <motion.p
                className="text-4xl md:text-5xl font-bold text-amber-100/90 mb-2 tracking-widest"
                style={{ 
                  fontFamily: 'serif',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.8), 0 0 20px rgba(139,0,0,0.5)',
                }}
                animate={{ opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 2.5, repeat: Infinity }}
              >
                武士道
              </motion.p>
              
              {/* English text */}
              <motion.p
                className="text-sm md:text-base tracking-[0.4em] text-amber-100/50 uppercase mb-8"
                style={{ 
                  fontFamily: 'serif',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                }}
              >
                The Way of the Warrior
              </motion.p>

              {/* Crimson progress bar with brush stroke ends */}
              <div className="relative w-56 mx-auto">
                {/* Brush stroke decorations */}
                <motion.div
                  className="absolute -left-4 top-1/2 -translate-y-1/2 w-4 h-3 bg-red-800/60"
                  style={{ 
                    clipPath: 'polygon(100% 0, 100% 100%, 0 50%)',
                  }}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                />
                <motion.div
                  className="absolute -right-4 top-1/2 -translate-y-1/2 w-4 h-3 bg-red-800/60"
                  style={{ 
                    clipPath: 'polygon(0 0, 0 100%, 100% 50%)',
                  }}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                />
                
                {/* Progress track */}
                <div className="h-[3px] bg-zinc-800/80 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full rounded-full"
                    style={{
                      background: 'linear-gradient(90deg, #7f1d1d, #dc2626, #b91c1c, #7f1d1d)',
                      boxShadow: '0 0 10px rgba(220,38,38,0.6), 0 0 20px rgba(139,0,0,0.4)',
                    }}
                    initial={{ width: '0%' }}
                    animate={{ width: `${Math.min(loadingProgress, 100)}%` }}
                    transition={{ duration: 0.1 }}
                  />
                </div>
              </div>

              {/* Percentage in Japanese style */}
              <motion.p
                className="mt-4 text-lg font-bold text-red-600/80 tracking-widest"
                style={{ 
                  fontFamily: 'serif',
                  textShadow: '0 0 10px rgba(220,38,38,0.5)',
                }}
              >
                {Math.min(Math.round(loadingProgress), 100)}%
              </motion.p>
            </motion.div>
          </div>

          {/* Brush stroke corner accents */}
          <motion.div
            className="absolute top-6 left-6"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <svg width="60" height="60" viewBox="0 0 60 60" className="opacity-30">
              <path d="M5 5 L5 40 M5 5 L40 5" stroke="#8B0000" strokeWidth="3" fill="none" strokeLinecap="round"/>
            </svg>
          </motion.div>
          <motion.div
            className="absolute bottom-6 right-6"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <svg width="60" height="60" viewBox="0 0 60 60" className="opacity-30">
              <path d="M55 55 L55 20 M55 55 L20 55" stroke="#8B0000" strokeWidth="3" fill="none" strokeLinecap="round"/>
            </svg>
          </motion.div>

          {/* Side kanji decorations */}
          <motion.div
            className="absolute top-1/2 left-6 -translate-y-1/2 text-5xl text-red-900/20 font-bold writing-vertical"
            style={{ writingMode: 'vertical-rl' }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.7 }}
          >
            侍魂
          </motion.div>
          <motion.div
            className="absolute top-1/2 right-6 -translate-y-1/2 text-5xl text-red-900/20 font-bold"
            style={{ writingMode: 'vertical-rl' }}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.7 }}
          >
            剣道
          </motion.div>

          {/* Exit ink wash effect */}
          {phase === 'exit' && (
            <motion.div
              className="absolute inset-0 pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.5, 0] }}
              transition={{ duration: 0.5 }}
              style={{
                background: 'radial-gradient(circle at 50% 50%, rgba(139,0,0,0.3) 0%, transparent 70%)',
              }}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;
